import { Outlet, Route, Routes, useNavigate } from 'react-router-dom';

import { Markdown } from '@client:common/Markdown';
import { Notification } from '@client:common/overlay/notification';
import { GlobalStore } from '@client:common/global-store';
import { ProgressIndicator } from '@client:common/overlay/progress-indicator';

import { AccountManagerService } from './services/AccountManagerService';
import { EmailCollector } from './views/email-collector/EmailCollector';
import { Registration } from './views/registration/Registration';
import { CreateAccountDto } from './models/CreateAccountDto';
import { PasswordReset } from './views/password-reset/PasswordReset';
import { AccountVerification } from './views/user-verification/AccountVerification';

import './AccountManager.dark.scss';
import './AccountManager.light.scss';

export function AccountManager() {
  const navigate = useNavigate();
  const onSendPasswordRecoveryEmail = (email: string) => {
    const accountManagerService = AccountManagerService.getInstance();

    ProgressIndicator.show();
    accountManagerService
      .sendPasswordRecoveryEmail(email)
      .then(() => {
        setTimeout(() => {
          navigate(`/pages/account/recovery/password?email=${email}`);
        }, 1000);
      })
      .catch(() => {
        Notification.open(
          <Markdown>
            There was a problem sending a password recovery email
          </Markdown>
        );
      })
      .finally(() => {
        ProgressIndicator.hide();
      });
  };

  const onCreateAccount = (createAccountDto: CreateAccountDto) => {
    const accountManagerService = AccountManagerService.getInstance();

    ProgressIndicator.show();
    accountManagerService
      .createAccount(createAccountDto)
      .then((account) => {
        const globalStore = GlobalStore.getInstance();

        globalStore.update({
          authenticatedAccount: account
        });
        Notification.open(
          <Markdown>
            Your account has been created and an email verification code has
            been sent to your email address, you will be re-directed to the
            email verification page in 5 seconds.
          </Markdown>
        );
        setTimeout(() => {
          navigate(
            `/pages/account/verification?email=${createAccountDto.email}`
          );
        }, 5000);
      })
      .catch((reason) => {
        switch (reason) {
          case 'user already exists':
            Notification.open(
              <Markdown>
                {`An account with email address\n**${createAccountDto.email}** already exists`}
              </Markdown>
            );
            break;

          default:
            Notification.open(
              <Markdown>There was a problem creating your account</Markdown>
            );
            break;
        }
      })
      .finally(() => {
        ProgressIndicator.hide();
      });
  };

  const onChangePassword = (
    username: string,
    verificationCode: string,
    password: string
  ) => {
    const accountManagerService = AccountManagerService.getInstance();

    ProgressIndicator.show();
    accountManagerService
      .changePassword(username, verificationCode, password)
      .then(() => {
        Notification.open(<Markdown>Password successfully changed</Markdown>);
      })
      .catch((reason) => {
        if (reason.includes('invalid verification code')) {
          Notification.open(
            <Markdown>
              Verification code is invalid, please try again with a correct code
            </Markdown>
          );
        } else {
          Notification.open(
            <Markdown>There was a problem changing your password</Markdown>
          );
        }
      })
      .finally(() => {
        ProgressIndicator.hide();
      });
  };

  const onVerifyAccount = (username: string, code: string) => {
    const accountManagerService = AccountManagerService.getInstance();

    ProgressIndicator.show();
    accountManagerService
      .verifyAccount(username, code)
      .then(() => {
        const globalStore = GlobalStore.getInstance();
        const currentAccount = globalStore.valueOf('authenticatedAccount');

        currentAccount.isVerified = true;
        Notification.open(
          <Markdown>
            Your account has been verified, you can now log in
          </Markdown>
        );
      })
      .catch((reason) => {
        if (reason.includes('invalid verification code')) {
          Notification.open(
            <Markdown>
              Verification code is invalid, please try again with a correct code
            </Markdown>
          );
        } else {
          Notification.open(
            <Markdown>
              There was a problem verifying your code! Please click on the *SEND
              ME A NEW CODE* button to receive a new code.
            </Markdown>
          );
        }
      })
      .finally(() => {
        ProgressIndicator.hide();
      });
  };

  const onResendVerificationCode = (username: string) => {
    const accountManagerService = AccountManagerService.getInstance();

    ProgressIndicator.show();
    accountManagerService
      .resendVerificationCode(username)
      .then(() => {
        Notification.open(
          <Markdown>
            Verification code has been sent to the email associated with your
            account
          </Markdown>
        );
      })
      .catch(() => {
        Notification.open(
          <Markdown>
            We were unable to send the verification code to the email address
            associated with your account
          </Markdown>
        );
      })
      .finally(() => {
        ProgressIndicator.hide();
      });
  };

  return (
    <Routes>
      <Route
        path='/*'
        element={
          <div className='account-manager'>
            <Outlet />
          </div>
        }
      >
        <Route
          path='registration'
          element={<Registration onSubmit={onCreateAccount} />}
        />
        <Route
          path='verification'
          element={
            <AccountVerification
              onSubmit={onVerifyAccount}
              onResendVerificationCode={onResendVerificationCode}
            />
          }
        />
        <Route
          path='recovery/password'
          element={<PasswordReset onSubmit={onChangePassword} />}
        />
        <Route
          path='recovery/request/password'
          element={<EmailCollector onSubmit={onSendPasswordRecoveryEmail} />}
        />
      </Route>
    </Routes>
  );
}
